package dev.helpmycity.`data`.local.database

import androidx.room3.EntityDeleteOrUpdateAdapter
import androidx.room3.EntityInsertAdapter
import androidx.room3.EntityUpsertAdapter
import androidx.room3.RoomDatabase
import androidx.room3.coroutines.createFlow
import androidx.room3.util.getColumnIndexOrThrow
import androidx.room3.util.performSuspending
import androidx.sqlite.SQLiteStatement
import kotlin.Int
import kotlin.Long
import kotlin.String
import kotlin.Suppress
import kotlin.Unit
import kotlin.collections.List
import kotlin.collections.MutableList
import kotlin.collections.mutableListOf
import kotlin.reflect.KClass
import kotlinx.coroutines.flow.Flow

@Suppress(names = ["UNCHECKED_CAST", "DEPRECATION", "REDUNDANT_PROJECTION", "REMOVAL", "MemberExtensionConflict"])
internal class NeighborhoodDao_Impl(
  __db: RoomDatabase,
) : NeighborhoodDao {
  private val __db: RoomDatabase

  private val __upsertAdapterOfNeighborhoodEntity: EntityUpsertAdapter<NeighborhoodEntity>
  init {
    this.__db = __db
    this.__upsertAdapterOfNeighborhoodEntity = EntityUpsertAdapter<NeighborhoodEntity>(object : EntityInsertAdapter<NeighborhoodEntity>() {
      protected override fun createQuery(): String = "INSERT INTO `neighborhoods` (`id`,`name`,`council_district`,`boundary_geojson`,`sync_state`,`last_synced_at`,`remote_version`) VALUES (?,?,?,?,?,?,?)"

      protected override fun bind(statement: SQLiteStatement, entity: NeighborhoodEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        val _tmpCouncilDistrict: String? = entity.councilDistrict
        if (_tmpCouncilDistrict == null) {
          statement.bindNull(3)
        } else {
          statement.bindText(3, _tmpCouncilDistrict)
        }
        val _tmpBoundaryGeoJson: String? = entity.boundaryGeoJson
        if (_tmpBoundaryGeoJson == null) {
          statement.bindNull(4)
        } else {
          statement.bindText(4, _tmpBoundaryGeoJson)
        }
        statement.bindText(5, entity.syncState)
        val _tmpLastSyncedAtMillis: Long? = entity.lastSyncedAtMillis
        if (_tmpLastSyncedAtMillis == null) {
          statement.bindNull(6)
        } else {
          statement.bindLong(6, _tmpLastSyncedAtMillis)
        }
        val _tmpRemoteVersion: String? = entity.remoteVersion
        if (_tmpRemoteVersion == null) {
          statement.bindNull(7)
        } else {
          statement.bindText(7, _tmpRemoteVersion)
        }
      }
    }, object : EntityDeleteOrUpdateAdapter<NeighborhoodEntity>() {
      protected override fun createQuery(): String = "UPDATE `neighborhoods` SET `id` = ?,`name` = ?,`council_district` = ?,`boundary_geojson` = ?,`sync_state` = ?,`last_synced_at` = ?,`remote_version` = ? WHERE `id` = ?"

      protected override fun bind(statement: SQLiteStatement, entity: NeighborhoodEntity) {
        statement.bindText(1, entity.id)
        statement.bindText(2, entity.name)
        val _tmpCouncilDistrict: String? = entity.councilDistrict
        if (_tmpCouncilDistrict == null) {
          statement.bindNull(3)
        } else {
          statement.bindText(3, _tmpCouncilDistrict)
        }
        val _tmpBoundaryGeoJson: String? = entity.boundaryGeoJson
        if (_tmpBoundaryGeoJson == null) {
          statement.bindNull(4)
        } else {
          statement.bindText(4, _tmpBoundaryGeoJson)
        }
        statement.bindText(5, entity.syncState)
        val _tmpLastSyncedAtMillis: Long? = entity.lastSyncedAtMillis
        if (_tmpLastSyncedAtMillis == null) {
          statement.bindNull(6)
        } else {
          statement.bindLong(6, _tmpLastSyncedAtMillis)
        }
        val _tmpRemoteVersion: String? = entity.remoteVersion
        if (_tmpRemoteVersion == null) {
          statement.bindNull(7)
        } else {
          statement.bindText(7, _tmpRemoteVersion)
        }
        statement.bindText(8, entity.id)
      }
    })
  }

  public override suspend fun upsert(neighborhood: NeighborhoodEntity): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfNeighborhoodEntity.upsert(_connection, neighborhood)
  }

  public override suspend fun upsertAll(neighborhoods: List<NeighborhoodEntity>): Unit = performSuspending(__db, false, true) { _connection ->
    __upsertAdapterOfNeighborhoodEntity.upsert(_connection, neighborhoods)
  }

  public override fun observeAll(): Flow<List<NeighborhoodEntity>> {
    val _sql: String = "SELECT * FROM neighborhoods ORDER BY name ASC"
    return createFlow(__db, false, arrayOf("neighborhoods")) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfCouncilDistrict: Int = getColumnIndexOrThrow(_stmt, "council_district")
        val _columnIndexOfBoundaryGeoJson: Int = getColumnIndexOrThrow(_stmt, "boundary_geojson")
        val _columnIndexOfSyncState: Int = getColumnIndexOrThrow(_stmt, "sync_state")
        val _columnIndexOfLastSyncedAtMillis: Int = getColumnIndexOrThrow(_stmt, "last_synced_at")
        val _columnIndexOfRemoteVersion: Int = getColumnIndexOrThrow(_stmt, "remote_version")
        val _result: MutableList<NeighborhoodEntity> = mutableListOf()
        while (_stmt.step()) {
          val _item: NeighborhoodEntity
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpCouncilDistrict: String?
          if (_stmt.isNull(_columnIndexOfCouncilDistrict)) {
            _tmpCouncilDistrict = null
          } else {
            _tmpCouncilDistrict = _stmt.getText(_columnIndexOfCouncilDistrict)
          }
          val _tmpBoundaryGeoJson: String?
          if (_stmt.isNull(_columnIndexOfBoundaryGeoJson)) {
            _tmpBoundaryGeoJson = null
          } else {
            _tmpBoundaryGeoJson = _stmt.getText(_columnIndexOfBoundaryGeoJson)
          }
          val _tmpSyncState: String
          _tmpSyncState = _stmt.getText(_columnIndexOfSyncState)
          val _tmpLastSyncedAtMillis: Long?
          if (_stmt.isNull(_columnIndexOfLastSyncedAtMillis)) {
            _tmpLastSyncedAtMillis = null
          } else {
            _tmpLastSyncedAtMillis = _stmt.getLong(_columnIndexOfLastSyncedAtMillis)
          }
          val _tmpRemoteVersion: String?
          if (_stmt.isNull(_columnIndexOfRemoteVersion)) {
            _tmpRemoteVersion = null
          } else {
            _tmpRemoteVersion = _stmt.getText(_columnIndexOfRemoteVersion)
          }
          _item = NeighborhoodEntity(_tmpId,_tmpName,_tmpCouncilDistrict,_tmpBoundaryGeoJson,_tmpSyncState,_tmpLastSyncedAtMillis,_tmpRemoteVersion)
          _result.add(_item)
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun getById(id: String): NeighborhoodEntity? {
    val _sql: String = "SELECT * FROM neighborhoods WHERE id = ?"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        val _columnIndexOfId: Int = getColumnIndexOrThrow(_stmt, "id")
        val _columnIndexOfName: Int = getColumnIndexOrThrow(_stmt, "name")
        val _columnIndexOfCouncilDistrict: Int = getColumnIndexOrThrow(_stmt, "council_district")
        val _columnIndexOfBoundaryGeoJson: Int = getColumnIndexOrThrow(_stmt, "boundary_geojson")
        val _columnIndexOfSyncState: Int = getColumnIndexOrThrow(_stmt, "sync_state")
        val _columnIndexOfLastSyncedAtMillis: Int = getColumnIndexOrThrow(_stmt, "last_synced_at")
        val _columnIndexOfRemoteVersion: Int = getColumnIndexOrThrow(_stmt, "remote_version")
        val _result: NeighborhoodEntity?
        if (_stmt.step()) {
          val _tmpId: String
          _tmpId = _stmt.getText(_columnIndexOfId)
          val _tmpName: String
          _tmpName = _stmt.getText(_columnIndexOfName)
          val _tmpCouncilDistrict: String?
          if (_stmt.isNull(_columnIndexOfCouncilDistrict)) {
            _tmpCouncilDistrict = null
          } else {
            _tmpCouncilDistrict = _stmt.getText(_columnIndexOfCouncilDistrict)
          }
          val _tmpBoundaryGeoJson: String?
          if (_stmt.isNull(_columnIndexOfBoundaryGeoJson)) {
            _tmpBoundaryGeoJson = null
          } else {
            _tmpBoundaryGeoJson = _stmt.getText(_columnIndexOfBoundaryGeoJson)
          }
          val _tmpSyncState: String
          _tmpSyncState = _stmt.getText(_columnIndexOfSyncState)
          val _tmpLastSyncedAtMillis: Long?
          if (_stmt.isNull(_columnIndexOfLastSyncedAtMillis)) {
            _tmpLastSyncedAtMillis = null
          } else {
            _tmpLastSyncedAtMillis = _stmt.getLong(_columnIndexOfLastSyncedAtMillis)
          }
          val _tmpRemoteVersion: String?
          if (_stmt.isNull(_columnIndexOfRemoteVersion)) {
            _tmpRemoteVersion = null
          } else {
            _tmpRemoteVersion = _stmt.getText(_columnIndexOfRemoteVersion)
          }
          _result = NeighborhoodEntity(_tmpId,_tmpName,_tmpCouncilDistrict,_tmpBoundaryGeoJson,_tmpSyncState,_tmpLastSyncedAtMillis,_tmpRemoteVersion)
        } else {
          _result = null
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun count(): Int {
    val _sql: String = "SELECT COUNT(*) FROM neighborhoods"
    return performSuspending(__db, true, false) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        val _result: Int
        if (_stmt.step()) {
          val _tmp: Int
          _tmp = _stmt.getLong(0).toInt()
          _result = _tmp
        } else {
          _result = 0
        }
        _result
      } finally {
        _stmt.close()
      }
    }
  }

  public override suspend fun delete(id: String) {
    val _sql: String = "DELETE FROM neighborhoods WHERE id = ?"
    return performSuspending(__db, false, true) { _connection ->
      val _stmt: SQLiteStatement = _connection.prepare(_sql)
      try {
        var _argIndex: Int = 1
        _stmt.bindText(_argIndex, id)
        _stmt.step()
      } finally {
        _stmt.close()
      }
    }
  }

  public companion object {
    public fun getRequiredColumnConverters(): List<KClass<*>> = emptyList()

    public fun getRequiredDaoReturnTypeConverters(): List<KClass<*>> = emptyList()
  }
}
